import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, PageHeader, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { omit } from 'lodash';
import _module from '../configs/module';
import { DELETE_MERCHANT_USERS, WRITE_MERCHANT_USERS } from '../configs/scopes';
import { FormInstance } from 'antd/lib/form';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import { usePageState } from '../../core/hooks/usePageState';
import { refreshSystemListState } from '../../../stores/system-list/actions';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import { MchUserProvider, User } from '../../domain/admin-providers/mchUserProvider';
import UserForm, { CloseEvent, UserFormProps } from '../components/UserForm';
import { EventProvider } from '../../../event/eventProvider';
import { filter } from 'rxjs/operators';
import { useOperatorLogger } from '../../core/hooks/useOperatorLogger';
import PaginationTransformer from '../../core/utils/paginationTransformer';
import { useTranslation } from 'react-i18next';

const { confirm } = Modal;

interface PageOp extends CoreProTableState<User> {
}

const defaulUserFormProps: UserFormProps = {
  isEdit: false,
  visible: false,
};

// eslint-disable-next-line
export default () => {
  const { t } = useTranslation();
  const systemId = new URLSearchParams(useLocation().search).get('systemId');
  const dispatch = useDispatch();
  const systemList = useStore().getState().systemList;
  useEffect(() => {
    dispatch(refreshSystemListState());
  }, [systemList, dispatch]);
  const tableActionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  const logger = useOperatorLogger();
  const pageOp = usePageState<PageOp>('PAGE_REMEMBER', {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1,
    },
    needRequest: true,
  });
  const [userFormState, setUserFormState] = useState<UserFormProps>(
    defaulUserFormProps,
  );

  const refresh = () => {
    pageOp.update((s) => ({ ...s, needRequest: true }));
    tableActionRef.current?.reload();
  };

  const deleteUser = (record: User) => {
    confirm({
      title: `刪除?`,
      content: `確認刪除 ${record?.displayName} (編號: ${record?.id})?`,
      icon: <ExclamationCircleOutlined />,
      okText: '確認',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        MchUserProvider.delete(record.id)
          .then((rs) => {
            logger.log({
              action: '刪除',
              payload: { user: record, res: rs },
              systemId: record.systemId,
            });
            Modal.success({
              content: '刪除成功',
              okText: '確認',
            });
            refresh();
          })
          .catch((e) => {
            logger.log({
              action: '刪除',
              payload: { user: record, res: e },
              systemId: record.systemId,
            });
            throw e;
          });
      },
    });
  };

  useEffect(() => {
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === CloseEvent.symbol))
      .subscribe((x) => {
        setUserFormState(defaulUserFormProps);
        refresh();
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [searchFormRef, pageOp]);

  return (
    <>
      <PageHeader
        title={t('mch_permission.UserList')}
        subTitle={''}
        extra={<></>}
      />
      <CoreProTable<User>
        // manualRequest={!systemId}
        bordered
        formRef={searchFormRef}
        tableState={pageOp.get()}
        updateTableState={(state) => {
          pageOp.update((v) => ({ ...v, ...state }));
        }}
        actionRef={tableActionRef as any}
        rowKey='id'
        columns={[
          {
            title: '商戶代號',
            dataIndex: 'systemId',
            fixed: 'left',
            renderFormItem: (_, row, form) => (
              <SystemIdSearchList
                onChange={(value) =>
                  form.setFieldsValue({ [_.dataIndex as string]: value })
                }
                systemId={pageOp.get().params.systemId}
              />
            ),
            initialValue: systemId,
            align: 'center',
            fieldProps: {
              showSearch: true,
            },
          },
          {
            title: '帳號',
            dataIndex: 'username',
            fixed: 'left',
          },
          {
            title: '顯示名稱',
            dataIndex: 'displayName',
            fieldProps: {
              extra: '(關鍵字查詢)',
            },
          },
          {
            title: '電子信箱',
            dataIndex: 'email',
          },
          {
            title: '群組',
            dataIndex: 'groupDisplayName',
            render: (node, row) => row.group.displayName,
            filterMultiple: false,
            filters: [],
            fieldProps: {
              extra: '(關鍵字查詢)',
            },
          },
          {
            hideInSearch: true,
            key: 'operation',
            title: '操作',
            align: 'center',
            fixed: 'right',
            render: (text, record) =>

              <Space>
                <Button
                  size={'small'}
                  key='editable'
                  disabled={!_module.can([WRITE_MERCHANT_USERS])}
                  onClick={(x) => {
                    setUserFormState({
                      isEdit: true,
                      record: record,
                      visible: true,
                    });
                  }}
                >
                  編輯
                </Button>
                <Button
                  size={'small'}
                  key='delete'
                  disabled={!_module.can([DELETE_MERCHANT_USERS]) || record.id === _module.claims().sub}
                  danger={_module.can([DELETE_MERCHANT_USERS]) && record.id !== _module.claims().sub}
                  onClick={(x) => {
                    deleteUser(record);
                  }}
                >
                  刪除
                </Button>
              </Space>,

          },
        ]}
        toolBarRender={() => [
          _module.canRender(
            WRITE_MERCHANT_USERS,
            <Button
              key={1}
              type='primary'
              onClick={() => setUserFormState({ isEdit: false, visible: true })}
            >
              新增使用者
            </Button>,
          ),
        ]}
        requestData={(params) =>
          MchUserProvider.getList(
            PaginationTransformer.paramTransfer(params),
            pageOp.get().params.systemId,
          ).then((rs) => {
            const param = {
              page: params.current,
              limit: params.pageSize,
              systemId: systemId ?? undefined,
              ...omit(params, ['current', 'pageSize']),
            };
            logger.log({
              action: '查詢',
              payload: { req: param },
            });
            return PaginationTransformer.responseTransfer(rs);
          })
        }
      />
      <UserForm {...userFormState} />
    </>
  );
};
